
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Subject, User, Difficulty, UserStatus, UserRole } from '../types';
import { authService } from '../services/authService';
import { api } from '../services/api';
import Button from '../components/Button';
import BadgeCard from '../components/BadgeCard';
import { US_STATES, getStandardForState } from '../data/curriculumStandards';
import { TOPIC_SUGGESTIONS } from '../data/topicSuggestions';
import { getTheme } from '../utils/theme';
import RecommendationsPanel from '../components/RecommendationsPanel';
import InsightsPanel from '../components/InsightsPanel';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  const [selectedSubject, setSelectedSubject] = useState<Subject>(Subject.MATH);
  const [topic, setTopic] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [difficulty, setDifficulty] = useState<Difficulty>(Difficulty.MEDIUM);
  const [isTimed, setIsTimed] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);

  // Rate Limiting
  const [cooldown, setCooldown] = useState(0);

  // Dashboard Tab State
  const [activeTab, setActiveTab] = useState<'overview' | 'recommendations' | 'insights'>('overview');

  // Edit Profile States
  const [editName, setEditName] = useState('');
  const [editGrade, setEditGrade] = useState(4);
  const [editState, setEditState] = useState('TX');
  const [editGoal, setEditGoal] = useState(500);

  // Join Class State
  const [showJoinClassModal, setShowJoinClassModal] = useState(false);
  const [classCodeInput, setClassCodeInput] = useState('');
  const [joinMessage, setJoinMessage] = useState('');

  const handleJoinClass = async (e: React.FormEvent) => {
    e.preventDefault();
    setJoinMessage('');
    try {
      const result = await api.post('/classes/join', { classCode: classCodeInput });
      if (result.success) {
        setJoinMessage(result.message);
        setTimeout(() => {
          setShowJoinClassModal(false);
          setClassCodeInput('');
          setJoinMessage('');
        }, 2000);
      } else {
        setJoinMessage(result.message || 'Failed to join');
      }
    } catch (err: any) {
      setJoinMessage(err.response?.data?.message || 'Failed to join class');
    }
  };

  useEffect(() => {
    const initDashboard = async () => {
      // 1. Get cached user first for immediate render
      const cachedUser = authService.getCurrentUser();
      if (cachedUser) {
        // Redirect parents to parent dashboard
        if (cachedUser.role === UserRole.PARENT) {
          navigate('/parent');
          return;
        }

        setUser(cachedUser);
        setEditName(cachedUser.name);
        setEditGrade(cachedUser.grade);
        setEditState(cachedUser.state);
        setEditGoal(cachedUser.weeklyGoal || 500);
      }

      // 2. Fetch latest data from server to get new scores/points
      const freshUser = await authService.refreshUser();
      if (freshUser) {
        // Redirect parents to parent dashboard
        if (freshUser.role === UserRole.PARENT) {
          navigate('/parent');
          return;
        }

        setUser(freshUser);
        // Update form state with fresh data
        setEditName(freshUser.name);
        setEditGrade(freshUser.grade);
        setEditState(freshUser.state);
        setEditGoal(freshUser.weeklyGoal || 500);

        // Only show onboarding modal for students
        if (freshUser.status === UserStatus.ONBOARDING && freshUser.role === UserRole.STUDENT) {
          setShowProfileModal(true);
        }
      } else if (!cachedUser) {
        // If no cache and fetch failed, redirect to login
        navigate('/login');
      }
    };

    initDashboard();
  }, [navigate]);

  useEffect(() => {
    let timer: any;
    if (cooldown > 0) {
      timer = setInterval(() => setCooldown(c => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  // Calculate Subject Mastery
  const calculateMastery = (subject: Subject) => {
    if (!user) return 0;
    const scores = (user.recentScores || []).filter(s => s.subject === subject);
    if (scores.length === 0) return -1;
    const total = scores.reduce((acc, curr) => acc + curr.score, 0);
    return Math.round(total / scores.length);
  };

  const handleStartQuiz = () => {
    if (!user) return;
    if (!topic) {
      alert("Please enter a topic first!");
      return;
    }

    if (cooldown > 0) return;

    // Start Cooldown
    setCooldown(10);

    const currentMastery = calculateMastery(selectedSubject);

    const params = new URLSearchParams({
      subject: selectedSubject,
      topic: topic,
      grade: user.grade.toString(),
      state: user.state,
      difficulty: difficulty,
      timed: isTimed.toString(),
      mastery: currentMastery.toString()
    });
    navigate(`/quiz?${params.toString()}`);
  };

  const handleSaveProfile = () => {
    if (!user) return;

    const updates = {
      name: editName,
      grade: editGrade,
      state: editState,
      weeklyGoal: editGoal,
      status: user.status === UserStatus.ONBOARDING ? UserStatus.ACTIVE : user.status
    };

    authService.updateCurrentUser(updates);

    setUser({ ...user, ...updates });
    setShowProfileModal(false);
  };

  const getStateName = (code: string) => US_STATES.find(s => s.code === code)?.name || code;
  const currentTheme = getTheme(selectedSubject);

  if (!user) return null;

  const weeklyProgressPercent = Math.min(100, Math.round((user.points / (user.weeklyGoal || 500)) * 100));

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 relative">

      {/* Help Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl border-4 border-brand-accent/20">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-indigo-900"><i className="fas fa-question-circle mr-2 text-brand-blue"></i> Help & FAQs</h2>
              <button onClick={() => setShowHelpModal(false)} className="text-slate-400 hover:text-slate-600">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="bg-slate-50 p-4 rounded-xl">
                <h3 className="font-bold text-brand-blue mb-1">How do I start a quiz?</h3>
                <p className="text-sm text-slate-600">Select a subject (Math, Science, etc.) on the dashboard, type in a topic (like "Fractions"), and click "Generate Quiz". AI will create questions just for you!</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl">
                <h3 className="font-bold text-brand-blue mb-1">How are points earned?</h3>
                <p className="text-sm text-slate-600">You get points for every correct answer. Finish a quiz with 100% score to earn a special badge!</p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Button variant="primary" onClick={() => setShowHelpModal(false)}>Got it!</Button>
            </div>
          </div>
        </div>
      )}

      {/* History Details Modal */}
      {selectedHistoryItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className={`bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden border-4 ${getTheme(selectedHistoryItem.subject).border}`}>
            <div className={`p-6 text-white bg-gradient-to-r ${getTheme(selectedHistoryItem.subject).gradient}`}>
              <div className="flex justify-between items-start">
                <h2 className="text-2xl font-bold brand-font">Quiz Report</h2>
                <button onClick={() => setSelectedHistoryItem(null)} className="text-white/80 hover:text-white">
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
              <p className="opacity-90 mt-1">{selectedHistoryItem.date}</p>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <div className="inline-block p-4 rounded-full bg-slate-50 mb-2 shadow-inner">
                  <div className={`text-5xl font-bold ${selectedHistoryItem.score >= 80 ? 'text-green-500' : 'text-brand-accent'}`}>
                    {selectedHistoryItem.score}%
                  </div>
                </div>
                <h3 className="text-xl font-bold text-slate-800">{selectedHistoryItem.topic}</h3>
                <p className="text-slate-500 font-medium">{selectedHistoryItem.subject}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 p-3 rounded-xl text-center">
                  <p className="text-xs text-slate-400 font-bold uppercase">Difficulty</p>
                  <p className="font-bold text-slate-700">{selectedHistoryItem.difficulty || 'Medium'}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-xl text-center">
                  <p className="text-xs text-slate-400 font-bold uppercase">Correct</p>
                  <p className="font-bold text-slate-700">
                    {selectedHistoryItem.correctCount !== undefined
                      ? `${selectedHistoryItem.correctCount}/${selectedHistoryItem.totalQuestions}`
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 mb-6">
                <p className="text-sm text-indigo-800 text-center">
                  {selectedHistoryItem.score >= 80
                    ? "🌟 Amazing work! You've mastered this topic."
                    : "💪 Good practice! Try this topic again to improve."}
                </p>
              </div>

              <Button onClick={() => setSelectedHistoryItem(null)} className="w-full justify-center">Close Report</Button>
            </div>
          </div>
        </div>
      )}

      {/* Join Class Modal */}
      {showJoinClassModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl animate-fade-in border-4 border-teal-500/20">
            <h2 className="text-2xl font-bold text-teal-800 mb-4 flex items-center">
              <i className="fas fa-chalkboard-teacher mr-3"></i> Join a Class
            </h2>
            <p className="text-slate-500 text-sm mb-6">Enter the 6-character code shared by your teacher.</p>

            <form onSubmit={handleJoinClass}>
              <div className="mb-4">
                <input
                  type="text"
                  className="w-full text-center text-3xl font-mono tracking-widest uppercase py-3 border-2 border-slate-200 rounded-xl focus:border-teal-500 outline-none"
                  placeholder="CODE"
                  maxLength={6}
                  value={classCodeInput}
                  onChange={e => setClassCodeInput(e.target.value)}
                  required
                />
              </div>

              {joinMessage && (
                <div className={`text-center mb-4 text-sm font-bold ${joinMessage.includes('Success') ? 'text-green-600' : 'text-red-500'}`}>
                  {joinMessage}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">Join</Button>
                <Button variant="outline" type="button" onClick={() => setShowJoinClassModal(false)} className="flex-1">Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl animate-fade-in border-4 border-brand-blue/10">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-3 text-3xl">
                👤
              </div>
              <h2 className="text-2xl font-bold text-indigo-900">{user.status === UserStatus.ONBOARDING ? 'Setup Your Profile' : 'Student Profile'}</h2>
              <p className="text-slate-500 text-sm">Customize your learning experience</p>
            </div>

            <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">My Name</label>
                <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-brand-blue outline-none transition" placeholder="Enter your name" />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Grade Level</label>
                <select value={editGrade} onChange={e => setEditGrade(Number(e.target.value))} className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-brand-blue outline-none transition bg-white">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>{g === 0 ? 'Kindergarten' : `Grade ${g}`}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">State Curriculum</label>
                <select value={editState} onChange={e => setEditState(e.target.value)} className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-brand-blue outline-none transition bg-white">
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Weekly Point Goal</label>
                <input type="number" value={editGoal} onChange={e => setEditGoal(Number(e.target.value))} className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 focus:border-brand-blue outline-none transition" />
                <p className="text-xs text-slate-400 mt-1">Goal: {editGoal} points</p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <Button variant="primary" className="flex-1" onClick={handleSaveProfile}>
                {user.status === UserStatus.ONBOARDING ? 'Start Learning!' : 'Save Profile'}
              </Button>
              {user.status !== UserStatus.ONBOARDING && (
                <Button variant="outline" className="flex-1" onClick={() => setShowProfileModal(false)}>Cancel</Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 bg-brand-blue text-white p-8 rounded-3xl shadow-xl">
        <div className="flex items-start">
          <div className="mr-4 mt-1">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30 overflow-hidden">
              <img src="/logo.png" alt="Logo" className="h-12 w-12 object-contain" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold mb-1">Hi, {user.name}! 👋</h1>
              <button
                onClick={() => setShowProfileModal(true)}
                className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition text-xs"
                title="Edit Profile"
              >
                <i className="fas fa-pencil-alt"></i>
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {/* Grade Dropdown */}
              <div className="relative group">
                <select
                  value={user.grade}
                  onChange={async (e) => {
                    const newGrade = Number(e.target.value);
                    setUser({ ...user, grade: newGrade });
                    await authService.updateCurrentUser({ grade: newGrade });
                  }}
                  className="appearance-none bg-indigo-700/50 hover:bg-indigo-700/70 text-white pl-3 pr-8 py-1 rounded-full text-sm font-semibold border border-indigo-500/50 cursor-pointer outline-none focus:ring-2 focus:ring-white/20 transition-colors"
                >
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => (
                    <option key={g} value={g} className="text-slate-900 bg-white">
                      {g === 0 ? 'Kindergarten' : `Grade ${g}`}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-200 text-xs">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>

              {/* State Dropdown */}
              <div className="relative group">
                <select
                  value={user.state}
                  onChange={async (e) => {
                    const newState = e.target.value;
                    setUser({ ...user, state: newState });
                    await authService.updateCurrentUser({ state: newState });
                  }}
                  className="appearance-none bg-indigo-700/50 hover:bg-indigo-700/70 text-white pl-8 pr-8 py-1 rounded-full text-sm font-semibold border border-indigo-500/50 cursor-pointer outline-none focus:ring-2 focus:ring-white/20 transition-colors max-w-[160px] truncate"
                >
                  {US_STATES.map(s => (
                    <option key={s.code} value={s.code} className="text-slate-900 bg-white">
                      {s.name}
                    </option>
                  ))}
                </select>
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-200 text-xs">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-200 text-xs">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>

              <span className="bg-indigo-700/50 px-3 py-1 rounded-full text-sm font-semibold border border-indigo-500/50 flex items-center" title={`Aligned with ${getStandardForState(user.state)}`}>
                <i className="fas fa-book mr-1.5 text-xs text-brand-accent"></i>
                <span className="truncate max-w-[200px]">{getStandardForState(user.state)}</span>
              </span>
              {user.status === UserStatus.ONBOARDING && (
                <span className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                  Onboarding
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="mt-6 md:mt-0 flex gap-4">
          <button
            onClick={() => setShowHelpModal(true)}
            className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-xl border border-white/20 flex items-center gap-2 transition"
          >
            <i className="fas fa-question-circle"></i> Help
          </button>
          <button
            onClick={() => navigate('/leaderboard')}
            className="flex items-center bg-white/20 hover:bg-white/30 backdrop-blur-sm px-6 py-4 rounded-2xl border border-white/10 min-w-[160px] transition-all"
          >
            <span className="text-3xl mr-4 drop-shadow-md">🏆</span>
            <div className="text-left">
              <p className="text-xs font-bold uppercase tracking-wider text-indigo-200">Total Points</p>
              <p className="text-3xl font-bold">{user.points}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Dashboard Tabs */}
      <div className="flex bg-white p-1 rounded-2xl shadow-sm mb-8 border border-slate-100 max-w-2xl mx-auto">
        {(['overview', 'recommendations', 'insights'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all capitalize ${activeTab === tab
              ? 'bg-brand-blue text-white shadow-md'
              : 'text-slate-500 hover:bg-slate-50'
              }`}
          >
            {tab === 'overview' && <i className="fas fa-home mr-2"></i>}
            {tab === 'recommendations' && <i className="fas fa-lightbulb mr-2"></i>}
            {tab === 'insights' && <i className="fas fa-chart-line mr-2"></i>}
            {tab}
          </button>
        ))}
      </div>

      {/* Main Content Grid */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Quiz Creator */}
          <div className="lg:col-span-2 space-y-8">

            {/* Goal Setting & Progress */}
            <section className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row gap-6 items-center">
              <div className="flex-1 w-full">
                <div className="flex justify-between items-end mb-2">
                  <h3 className="text-lg font-bold text-indigo-900">Weekly Goal: {user.weeklyGoal || 500} pts</h3>
                  <span className="text-xs font-bold text-slate-400">{weeklyProgressPercent}% Achieved</span>
                </div>
                <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-green shadow-[0_0_10px_rgba(52,211,153,0.5)] transition-all duration-1000"
                    style={{ width: `${weeklyProgressPercent}%` }}
                  ></div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowProfileModal(true)}>
                Change Goal
              </Button>
            </section>

            <section className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
              <h2 className="text-2xl font-bold text-indigo-900 mb-6 flex items-center">
                <i className="fas fa-rocket mr-3 text-brand-accent"></i>
                Start a New Adventure
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Pick a Subject</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.values(Subject).map((sub) => {
                      const theme = getTheme(sub);
                      const isSelected = selectedSubject === sub;

                      return (
                        <button
                          key={sub}
                          onClick={() => setSelectedSubject(sub)}
                          className={`p-3 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden group ${isSelected
                            ? `${theme.lightBg} ${theme.border} ${theme.text} shadow-md`
                            : 'border-slate-100 hover:border-slate-300 text-slate-500 hover:bg-slate-50'
                            }`}
                        >
                          <div className={`text-3xl mb-2 transition-transform duration-300 group-hover:scale-110 ${isSelected ? theme.text : 'text-slate-400'}`}>
                            <i className={theme.icon}></i>
                          </div>
                          <div className="text-sm font-bold">{sub}</div>
                          {isSelected && (
                            <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${theme.gradient}`}></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="relative">
                  <label className="block text-sm font-bold text-slate-700 mb-2">What do you want to practice?</label>
                  <div className="relative group">
                    <div className={`absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center rounded-l-xl transition-colors ${currentTheme.lightBg}`}>
                      <i className={`fas fa-search ${currentTheme.text}`}></i>
                    </div>
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => {
                        setTopic(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      placeholder={`e.g. ${TOPIC_SUGGESTIONS[selectedSubject]?.[0] || 'Fractions'}...`}
                      className="w-full pl-14 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-brand-blue focus:ring-0 outline-none transition-colors"
                    />

                    {/* Topic Suggestions Dropdown */}
                    {showSuggestions && (
                      <div className="absolute z-20 left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 max-h-60 overflow-y-auto animate-fade-in">
                        {TOPIC_SUGGESTIONS[selectedSubject]
                          ?.filter(t => t.toLowerCase().includes(topic.toLowerCase()))
                          .map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setTopic(suggestion);
                                setShowSuggestions(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center text-slate-700 font-medium"
                            >
                              <i className={`fas fa-lightbulb mr-3 text-xs ${currentTheme.text} opacity-50`}></i>
                              {suggestion}
                            </button>
                          ))}
                        {TOPIC_SUGGESTIONS[selectedSubject]?.filter(t => t.toLowerCase().includes(topic.toLowerCase())).length === 0 && topic.length > 0 && (
                          <div className="px-4 py-3 text-slate-400 text-sm italic">
                            Press "Generate Quiz" to use "{topic}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Quiz Configuration: Difficulty & Timer */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Difficulty</label>
                    <div className="flex bg-slate-100 p-1 rounded-xl">
                      {Object.values(Difficulty).map(lvl => (
                        <button
                          key={lvl}
                          onClick={() => setDifficulty(lvl)}
                          className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${difficulty === lvl
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                          {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex-none min-w-[140px]">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Mode</label>
                    <button
                      onClick={() => setIsTimed(!isTimed)}
                      className={`w-full py-2.5 px-4 rounded-xl border-2 flex items-center justify-center transition-all ${isTimed
                        ? 'bg-red-50 border-brand-red text-brand-red font-bold'
                        : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                    >
                      <i className={`fas fa-stopwatch mr-2 ${isTimed ? 'animate-pulse' : ''}`}></i>
                      {isTimed ? 'Timed Mode' : 'Standard'}
                    </button>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    onClick={handleStartQuiz}
                    disabled={!topic.trim() || cooldown > 0}
                    className={`w-full flex items-center justify-center py-4 text-lg font-bold rounded-xl text-white shadow-lg shadow-indigo-200 transition-all hover:scale-[1.02] active:scale-95 bg-gradient-to-r ${currentTheme.gradient} disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {cooldown > 0 ? `Wait ${cooldown}s` : (
                      <>Generate Quiz with AI <i className="fas fa-magic ml-2 animate-pulse"></i></>
                    )}
                  </button>

                  <div className="mt-3 flex items-center justify-center text-xs text-slate-500">
                    <i className={`fas fa-robot mr-1.5 ${currentTheme.text}`}></i>
                    AI will generate <strong>{difficulty}</strong> questions based on <strong className="mx-1">{getStateName(user.state)}</strong> standards.
                    {calculateMastery(selectedSubject) > 80 && (
                      <span className="ml-2 text-green-600 bg-green-50 px-2 py-0.5 rounded-md font-bold border border-green-200">
                        <i className="fas fa-arrow-up mr-1"></i>Adaptive Mode
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Flashcards Promo */}
            <section className="bg-brand-light p-6 rounded-3xl border border-indigo-100 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 text-9xl text-indigo-200 opacity-20 pointer-events-none">
                <i className="fas fa-clone"></i>
              </div>
              <div className="flex-1 relative z-10">
                <h3 className="text-xl font-bold text-indigo-900 mb-2">
                  <i className="fas fa-layer-group mr-2"></i> Study with Flashcards
                </h3>
                <p className="text-slate-600 text-sm">Need to memorize state capitals, math formulas, or vocabulary? Generate a custom deck instantly.</p>
              </div>
              <div className="relative z-10">
                <Button variant="primary" onClick={() => navigate(`/flashcards?grade=${user.grade}&state=${user.state}`)}>
                  Create Deck <i className="fas fa-arrow-right ml-2"></i>
                </Button>
              </div>
            </section>

            {/* Recent Activity */}
            <section>
              <h3 className="text-xl font-bold text-slate-700 mb-4">Quiz History</h3>
              <div className="bg-white rounded-2xl shadow p-4 divide-y divide-slate-100">
                {(user.recentScores || []).length > 0 ? (
                  (user.recentScores || []).slice().reverse().slice(0, 5).map((score, idx) => {
                    const itemTheme = getTheme(score.subject || Subject.MATH);
                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedHistoryItem(score)}
                        className="w-full py-3 flex justify-between items-center hover:bg-slate-50 transition-colors rounded-lg px-2 group"
                      >
                        <div className="flex items-center text-left">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 font-bold text-lg ${itemTheme.lightBg} ${itemTheme.text}`}>
                            <i className={itemTheme.icon}></i>
                          </div>
                          <div>
                            <p className="font-bold text-slate-800 group-hover:text-brand-blue transition-colors">{score.topic || 'General Quiz'}</p>
                            <p className="text-xs text-slate-500">{score.subject} • {score.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <span className={`font-bold mr-3 ${score.score >= 80 ? 'text-green-500' : 'text-brand-accent'}`}>{score.score}%</span>
                          <i className="fas fa-chevron-right text-slate-300 text-xs"></i>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="py-8 text-center text-slate-400">
                    <p>No quizzes taken yet. Start your first adventure!</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Right Column: Gamification & Stats */}
          <div className="space-y-8">

            {/* Join Class Card */}
            <section className="bg-slate-800 text-white p-6 rounded-3xl shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-2 -mr-2 text-6xl text-white/5 pointer-events-none">
                <i className="fas fa-school"></i>
              </div>
              <div className="relative z-10">
                <h3 className="font-bold text-lg mb-1">Have a Class Code?</h3>
                <p className="text-slate-300 text-sm mb-4">Join your teacher's classroom to get assignments.</p>
                <Button size="sm" onClick={() => setShowJoinClassModal(true)} className="w-full bg-teal-500 hover:bg-teal-600 border-none text-white">
                  Enter Code
                </Button>
              </div>
            </section>

            {/* Mastery Chart */}
            <section className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
              <h2 className="text-xl font-bold text-indigo-900 mb-4">Subject Mastery</h2>
              <div className="space-y-5">
                {Object.values(Subject).map(sub => {
                  const mastery = calculateMastery(sub);
                  const hasData = mastery !== -1;
                  const displayMastery = hasData ? mastery : 0;
                  const theme = getTheme(sub);

                  return (
                    <div key={sub}>
                      <div className="flex justify-between text-xs font-bold mb-1.5">
                        <span className="flex items-center text-slate-700">
                          <i className={`${theme.icon} ${theme.text} w-5 text-center mr-1`}></i> {sub}
                        </span>
                        <span className={displayMastery >= 80 ? 'text-green-500' : 'text-slate-400'}>
                          {hasData ? `${displayMastery}%` : '-'}
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-1000 ${displayMastery >= 80 ? 'bg-green-400' :
                            displayMastery >= 50 ? theme.bg : 'bg-slate-300'
                            }`}
                          style={{ width: `${displayMastery}%` }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-50 text-center">
                <p className="text-xs text-slate-400">Calculated from your recent quiz scores.</p>
              </div>
            </section>

            <section className="bg-white p-6 rounded-3xl shadow-lg border border-slate-100">
              <h2 className="text-xl font-bold text-indigo-900 mb-4">My Badges</h2>
              <div className="grid grid-cols-2 gap-3">
                {(user.badges || []).map(badge => (
                  <BadgeCard key={badge.id} badge={badge} />
                ))}
              </div>
            </section>

            <section className="bg-gradient-to-br from-purple-600 to-indigo-600 p-6 rounded-3xl shadow-lg text-white">
              <h2 className="text-xl font-bold mb-4">Daily Challenge ⚡</h2>
              <p className="text-purple-100 mb-4 text-sm">Complete a Language Arts quiz to earn double points today!</p>
              <div className="w-full bg-black/20 rounded-full h-3 mb-4">
                <div className="bg-brand-accent h-3 rounded-full w-1/3 shadow-[0_0_10px_rgba(251,191,36,0.5)]"></div>
              </div>
              <p className="text-xs text-right font-bold mb-4">1/3 Completed</p>
              <Button variant="secondary" size="sm" className="w-full">
                Go to Challenge
              </Button>
            </section>
          </div>
        </div>
      )}

      {/* Recommendations Tab */}
      {
        activeTab === 'recommendations' && (
          <div className="max-w-5xl mx-auto">
            <RecommendationsPanel />
          </div>
        )
      }

      {/* Insights Tab */}
      {
        activeTab === 'insights' && (
          <div className="max-w-5xl mx-auto">
            <InsightsPanel />
          </div>
        )
      }
    </div >
  );
};

export default Dashboard;
